/*
 * The typed REST client.
 *
 * `openapi-fetch` is given the generated `paths` type, so every call is checked
 * against `openapi.json` — request bodies, path/query params, and response
 * shapes are all derived from the schema (no `any` on the API boundary). A
 * single middleware:
 *   - attaches `Authorization: Bearer <jwt>` from the token store, and
 *   - intercepts 401 responses to clear the token (→ the auth context logs out
 *     and the route guard bounces to /login).
 *
 * Consumers import `api` (the low-level client) or the typed helpers below.
 */

import createClient, { type Middleware } from "openapi-fetch";
import type { paths, components } from "./schema";
import { API_BASE } from "./config";
import { getToken, clearToken } from "../auth/tokenStore";

/** Convenience aliases for the generated schema objects (used app-wide). */
export type Schemas = components["schemas"];
export type Role = Schemas["Role"];
export type UserResponse = Schemas["UserResponse"];
export type MembershipResponse = Schemas["MembershipResponse"];
export type BrandResponse = Schemas["BrandResponse"];
export type TokenResponse = Schemas["TokenResponse"];
export type ChangePasswordResponse = Schemas["ChangePasswordResponse"];
export type CreateBrandResponse = Schemas["CreateBrandResponse"];
export type ProvisionUserRequest = Schemas["ProvisionUserRequest"];
export type RunSummary = Schemas["RunSummary"];
export type RunListResponse = Schemas["RunListResponse"];
export type RunDetail = Schemas["RunDetail"];
export type RunCreate = Schemas["RunCreate"];
export type RunConfirm = Schemas["RunConfirm"];
export type PreviewTicketsResponse = Schemas["PreviewTicketsResponse"];
export type NotionTicketRow = Schemas["NotionTicketRow"];
export type ControlResponse = Schemas["ControlResponse"];
export type InconsistencyReportResponse = Schemas["InconsistencyReportResponse"];
export type InconsistencyRow = Schemas["InconsistencyRow"];
export type ProductConfigResponse = Schemas["ProductConfigResponse"];
export type CredentialResponse = Schemas["CredentialResponse"];
export type MemberRosterEntry = Schemas["MemberRosterEntry"];
export type UpdateMemberRequest = Schemas["UpdateMemberRequest"];
export type UserRosterEntry = Schemas["UserRosterEntry"];
export type UpdateUserRequest = Schemas["UpdateUserRequest"];
// Launch-wizard refinements (2026-06-19-003): Admanage Settings dropdowns + the
// prep-then-confirm Preview surface.
export type RefreshTicketsRequest = Schemas["RefreshTicketsRequest"];
export type ProfileOption = Schemas["ProfileOption"];
export type ProfilesResponse = Schemas["ProfilesResponse"];
export type CampaignOption = Schemas["CampaignOption"];
export type CampaignsResponse = Schemas["CampaignsResponse"];
// The URL dropdown + a ticket's Campaign / Landing Pages (2026-10-19).
export type LandingPageOption = Schemas["LandingPageOption"];
export type LandingPagesResponse = Schemas["LandingPagesResponse"];
export type TicketCampaignRef = Schemas["TicketCampaignRef"];
export type TicketLandingPageRef = Schemas["TicketLandingPageRef"];
export type AdsetOption = Schemas["AdsetOption"];
export type AdsetsResponse = Schemas["AdsetsResponse"];
// Settings prefill from the last run (2026-07-07 #4).
export type LastRunDefaultsResponse = Schemas["LastRunDefaultsResponse"];
export type RunPreviewResponse = Schemas["RunPreviewResponse"];
export type PreviewTicketEntry = Schemas["PreviewTicketEntry"];
export type PreviewAdEntry = Schemas["PreviewAdEntry"];
// Hygiene scan (plan 2026-07-02-001): the flagged-tickets list + scan-now.
export type HygieneFlagRow = Schemas["HygieneFlagRow"];
export type HygieneFlagsResponse = Schemas["HygieneFlagsResponse"];
export type VideoSyncResponse = Schemas["VideoSyncResponse"];
export type VideoSyncTicketRow = Schemas["VideoSyncTicketRow"];
export type VideoSyncMode = VideoSyncResponse["mode"];
export type VideoTicketPerformance = Schemas["VideoTicketPerformance"];
export type VideoPerfWindow = Schemas["VideoPerfWindow"];
export type VideoSyncModeResponse = Schemas["VideoSyncModeResponse"];
export type HygieneScanResponse = Schemas["HygieneScanResponse"];
// Truth-debt ledger (batch 5 #8): bookkeep claims that never landed.
export type BookkeepDebtRow = Schemas["BookkeepDebtRow"];
export type BookkeepDebtsResponse = Schemas["BookkeepDebtsResponse"];
// Creative Analysis coverage (plan 2026-07-22-001): per-product snapshot
// summaries, the full explorer blob, and the operator recompute 202.
export type CoverageMetaSummary = Schemas["CoverageMetaSummary"];
export type CoverageProductSummary = Schemas["CoverageProductSummary"];
export type CoverageListResponse = Schemas["CoverageListResponse"];
export type CoverageSnapshotResponse = Schemas["CoverageSnapshotResponse"];
export type CoverageSyncAccepted = Schemas["CoverageSyncAccepted"];
export type CoverageSyncStatusResponse = Schemas["CoverageSyncStatusResponse"];
// Concept-alias ledger (batch 5 #7): teach/list/forget + sheet-backed suggest.
export type ConceptAliasRow = Schemas["ConceptAliasRow"];
export type ConceptAliasListResponse = Schemas["ConceptAliasListResponse"];
export type TeachConceptAliasRequest = Schemas["TeachConceptAliasRequest"];
export type AliasSuggestCandidate = Schemas["AliasSuggestCandidate"];
export type ConceptAliasSuggestResponse = Schemas["ConceptAliasSuggestResponse"];
// Saved reports (2026-10-28): named, shared Creative Analysis views.
export type SavedReportRow = Schemas["SavedReportRow"];
export type SavedReportListResponse = Schemas["SavedReportListResponse"];

/** Raised when an API call returns a non-2xx; carries status + parsed error. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  onResponse({ response }) {
    // A 401 means the token is missing/expired/revoked — drop it so the auth
    // context reacts (logout → /login). Do NOT clear on 403 (that is a role
    // authorization failure on a valid session).
    if (response.status === 401) clearToken();
    return response;
  },
};

/** The raw typed client. Prefer `request()` / the resource helpers below. */
export const api = createClient<paths>({ baseUrl: API_BASE });
api.use(authMiddleware);

/**
 * Unwrap an openapi-fetch `{ data, error }` result into the typed `data`,
 * throwing `ApiError` on failure. Lets call sites `await` a value and rely on
 * TanStack Query / try-catch for errors.
 */
export function unwrap<T>(
  result: { data?: T; error?: unknown; response: Response },
): T {
  if (result.error !== undefined || result.data === undefined) {
    throw new ApiError(result.response.status, result.error);
  }
  return result.data;
}
